<template>
  <span id="re-billing-invoice" v-if="isVisible()">
    <el-dropdown-item id="re-billing-invoice-dropdown-item" @click.native="dialogVisible = true">
      <span class="more-actions-option">{{ $t('customerService.shipment.reBillInvoice') }}</span>
    </el-dropdown-item>
    <el-dialog
      :close-on-click-modal="false"
      v-on:open="handleOpen"
      :title="$t('customerService.shipment.reBillInvoice')"
      :visible.sync="dialogVisible"
      width="35%"
      class="dialog-with-cols"
      id="re-billing-invoice-dialog"
      append-to-body
    >
       <el-form :model="form" :rules="formRules" ref="form" label-position="right" size="mini">
        <el-row :gutter="10">
          <el-col :span="24">
            <div>
              {{ $t('customerService.shipment.selectCreditedInvoice') }}
            </div>
            <el-form-item prop="invoiceId">
               <el-select
                filterable
                v-model="invoiceSelected"
                value-key="number"
                :loading="loading"
                id="form-invoiceId-select"
                ref="invoiceId-select"
              >
                <el-option
                  v-for="invoice in invoiceList"
                  :key="invoice.number"
                  :label="invoice.number ? `${invoice.number}`: ''"
                  :value="invoice"
                ></el-option>
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>
        <el-row :gutter="10" v-if="!invoiceSelected.id">
          <el-col :span="24">
            <span> {{ $t('customerService.shipment.showingCreditedInvoicesFrom') }} </span>
            <span> {{ shipment.actualFilters.outboundDateFrom |  moment(dateDisplayFormat) }} </span>
            <span v-if="shipment.actualFilters.outboundDateTo"> - {{ shipment.actualFilters.outboundDateTo |  moment(dateDisplayFormat) }} </span>
          </el-col>
        </el-row>
        <el-row :gutter="10" v-if="invoiceSelected.id">
          <el-col :span="24">
            <br>
            <span class="span-small-list">
              <strong>{{ $t('invoices.creditAndReBill.originalInvoiceDetail') }}</strong>
            </span>
            <span class="span-small-list">
              <strong>{{ $t('invoices.creditAndReBill.invoiceNumber') }}</strong> {{ invoiceSelected.number }}
            </span>
            <span class="span-small-list">
              <strong>{{ $t('invoices.creditAndReBill.date') }}</strong> {{ invoiceSelected.date | moment(dateDisplayFormat) }}
            </span>
            <span class="span-small-list">
              <strong>{{ $t('invoices.creditAndReBill.billTo') }}</strong> {{ invoiceSelected.billTo }}
            </span>
            <span class="span-small-list">
              <strong>{{ $t('invoices.creditAndReBill.consignee') }}</strong> {{ invoiceSelected.consignee }}
            </span>
            <span class="span-small-list">
              <strong>{{ $t('invoices.creditAndReBill.loadNumber') }}</strong> {{ invoiceSelected.loadNumber }}
            </span>
          </el-col>
        </el-row>
      </el-form>
      <div id="add-dialog-footer" slot="footer" class="dialog-footer form-vertical-item flex-reverse-direction">
        <el-button id="re-bill-form-submit-button" type="primary" @click="submitForm(true)" size="mini">{{ $t('customerService.shipment.reBillInvoice') }}</el-button>
        <el-button id="re-bill-form-clear-button" @click="resetForm()" size="mini">{{ $t('common.clear') }}</el-button>
      </div>
    </el-dialog>
  </span>
</template>

<script>
import { Message } from 'element-ui';
import { mapGetters } from 'vuex';
import moment from 'moment';
import constants from '@/utils/constants';
import apiConstants from '@/utils/apiConstants';
import selectService from '@/api/selectService';
import { INVOICES_RE_BILL, SHIPMENT_SEARCH } from '@/store/actions';

export default {
  name: 'ReBillInvoice',
  data() {
    return {
      dialogVisible: false,
      invoiceList: [],
      invoiceSelected: {},
      loading: false,
      form: {
        invoiceId: null
      },
      formRules: {
        invoiceId: [
          {
            required: true,
            message: this.$t('customerService.shipment.error.invoiceRequired'),
            trigger: 'blur'
          }
        ]
      },
      dateDisplayFormat: constants.DATES.DEFAULT_DISPLAY_FORMAT
    };
  },
  computed: {
    ...mapGetters(['shipment']),
  },
  methods: {
    isVisible() {
      return this.$can(this.permissions.BILLING.REBILL_INVOICE);
    },
    handleOpen() {
      this.$nextTick(() => {
        this.resetForm();
      });
      this.searchInvoicesPendingToRebill();
    },
    async searchInvoicesPendingToRebill() {
      this.invoiceList = [{}];
      this.loading = true;
      try {
        const response = await selectService.getList(
          apiConstants.END_POINTS.BILLING.INVOICE_CREDITED,
          {
            invoiceDateFrom: moment(this.shipment.actualFilters.outboundDateFrom).format(constants.DATES.DEFAULT_BACKEND_FORMAT),
            invoiceDateTo: this.shipment.actualFilters.outboundDateTo ? moment(this.shipment.actualFilters.outboundDateTo).format(constants.DATES.DEFAULT_BACKEND_FORMAT) : moment(this.shipment.actualFilters.outboundDateFrom).format(constants.DATES.DEFAULT_BACKEND_FORMAT)
          }
        );
        if (response instanceof Array) {
          this.invoiceList = this.invoiceList.concat(response);
          this.$nextTick(() => {
            this.invoiceSelected = this.invoiceList.find(invoice => invoice.number === this.shipment.actualFilters.invoiceNumber) || {};
            if (!this.invoiceSelected.id) {
              this.setFocus();
            }
          });
          this.loading = false;
        }
      } catch {
        this.loading = false;
      }
    },
    async submitForm() {
      this.form.invoiceId = this.invoiceSelected.id;
      const validForm = await this.$refs.form.validate().catch(() => {});
      if (!validForm) {
        return;
      }
      Message.closeAll();
      const loading = this.$loading(constants.LOADING.DEFAULT_CONFIG);
      try {
        const response = await this.$store.dispatch(INVOICES_RE_BILL, this.form.invoiceId);
        this.$message.success(response.message);
        this.$store.dispatch(SHIPMENT_SEARCH, {});
        this.dialogVisible = false;
      } finally {
        loading.close();
      }
    },
    setFocus() {
      this.$nextTick(() => {
        this.$refs.form.$el[0].focus();
      });
    },
    resetForm(focus) {
      this.$refs.form.resetFields();
      this.invoiceSelected = {};
      if (focus) {
        this.setFocus();
      }
    }
  }
};
</script>
<style rel="stylesheet/scss" lang="scss" scoped>
.el-form-item {
  width: 70%;
  margin-top: 15px;
  margin-bottom: 10px;
  .el-form-item__content {
    width: 80%;
    margin: 0px;
  }
}
</style>
